import React, { useState } from 'react';
import api from '../config/axiosConfig';
import { showErrorMsg } from '../config/common';
import { API_URLS } from '../config/Constants';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any[];
  columns: Array<{
    header: string;
    key: string;
    dataKey?: string;
  }>;
  title: string;
  teamName?: string;
  seasonName?: string;
  sortConfig?: {
    key: string;
    direction: 'asc' | 'desc';
  } | null;
  clientId: string | null;
}

interface EmailForm {
  to: string;
  subject: string;
  message: string;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  data,
  columns,
  title,
  teamName = 'Team',
  seasonName = 'Season',
  sortConfig,
  clientId,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailForm, setEmailForm] = useState<EmailForm>({
    to: '',
    subject: `${teamName} - ${title} - Leaderboard Report`,
    message: `Please find attached the ${title} leaderboard report for ${teamName}.`,
  });
  const [emailStatus, setEmailStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  if (!isOpen) return null;

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    try {
      const reportData = {
        title,
        data,
        columns: columns.map((col) => ({
          header: col.header,
          key: col.key,
          dataKey: col.dataKey || col.key,
        })),
        teamName,
        seasonName,
        exportDate: new Date(),
        sortConfig,
        clientId,
        isLeaderboard: sortConfig ? true : false,
      };

      // Send data to backend for PDF generation
      const response = await api.post(
        API_URLS.EXPORT_DOWNLOAD,
        {
          reportData,
        },
        {
          responseType: 'blob',
          timeout: 60000,
        },
      );

      if (response.status === 200) {
        // Handle PDF download
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${teamName}-${title.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        onClose();
      } else {
        throw new Error(response.data?.error || 'Failed to generate PDF');
      }
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      const raw = error.response?.data?.error || error.message || 'Error generating PDF. Please try again.';
      const errorMessage =
        error?.code === 'ECONNABORTED' || /timeout/i.test(String(raw))
          ? 'The export timed out. Please try again.'
          : raw;
      showErrorMsg(typeof errorMessage === 'string' ? errorMessage : 'Error generating PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleEmailPDF = async () => {
    if (!emailForm.to.trim()) {
      setEmailStatus({
        type: 'error',
        message: 'Please enter an email address',
      });
      return;
    }

    setIsExporting(true);
    setEmailStatus({ type: null, message: '' });

    try {
      const reportData = {
        title,
        data,
        columns: columns.map((col) => ({
          header: col.header,
          key: col.key,
          dataKey: col.dataKey || col.key,
        })),
        teamName,
        seasonName,
        exportDate: new Date(),
        sortConfig,
        clientId,
        isLeaderboard: sortConfig ? true : false,
      };

      // Send data to backend for PDF generation and email
      const response = await api.post(
        API_URLS.EXPORT_EMAIL,
        {
          to: emailForm.to,
          subject: emailForm.subject,
          message: emailForm.message,
          reportData,
          filename: `${teamName}-${title.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`,
        },
        { timeout: 60000 },
      );

      const result = response.data;

      if (result.success) {
        setEmailStatus({
          type: 'success',
          message: 'Email sent successfully!',
        });
        onClose();
        setShowEmailForm(false);
        setEmailStatus({ type: null, message: '' });
      } else {
        setEmailStatus({
          type: 'error',
          message: result.error || 'Failed to send email',
        });
      }
    } catch (error: any) {
      console.error('Error sending email:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error sending email. Please try again.';
      setEmailStatus({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Export {title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {!showEmailForm ? (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">Export the {title} leaderboard as a PDF file</p>

              <div className="bg-gray-50 text-gray-600 border-gray-200 border p-3 rounded-md text-sm">
                <p>
                  <strong>Team:</strong> {teamName}
                </p>
                <p>
                  <strong>Season:</strong> {seasonName}
                </p>
                <p>
                  <strong>Report:</strong> {title}
                </p>
                <p>
                  <strong>Players:</strong> {data.length}
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleDownloadPDF}
                  disabled={isExporting}
                  className="flex-1 text-lg bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isExporting ? 'Generating...' : 'Download PDF'}
                </button>

                <button
                  onClick={() => setShowEmailForm(true)}
                  disabled={isExporting}
                  className="flex-1 text-lg bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Email PDF
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">Email PDF Report</h4>
                <button onClick={() => setShowEmailForm(false)} className="text-sm text-gray-500 hover:text-gray-700">
                  Back
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={emailForm.to}
                    onChange={(e) => setEmailForm((prev) => ({ ...prev, to: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="coach@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={emailForm.subject}
                    onChange={(e) =>
                      setEmailForm((prev) => ({
                        ...prev,
                        subject: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={emailForm.message}
                    onChange={(e) =>
                      setEmailForm((prev) => ({
                        ...prev,
                        message: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Optional message to include in the email..."
                  />
                </div>
              </div>

              {emailStatus.type && (
                <div
                  className={`p-3 rounded-md text-sm ${
                    emailStatus.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {emailStatus.message}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowEmailForm(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>

                <button
                  onClick={handleEmailPDF}
                  disabled={isExporting}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isExporting ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
